import { useState } from "react"
import { useMutation } from "urql"
import { Share2, Copy, Check } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CreatePortalLinkMutation } from "@/lib/graphql/mutations"

interface ShareLinkDialogProps {
  scopeType: "source" | "event" | "destination"
  scopeId: string
}

export function ShareLinkDialog({ scopeType, scopeId }: ShareLinkDialogProps) {
  const [open, setOpen] = useState(false)
  const [expiresIn, setExpiresIn] = useState("24h")
  const [shareUrl, setShareUrl] = useState("")
  const [copied, setCopied] = useState(false)
  const [, createLink] = useMutation(CreatePortalLinkMutation)

  const handleCreate = async () => {
    const scope: Record<string, string> = {}
    if (scopeType === "source") scope.sourceId = scopeId
    if (scopeType === "event") scope.eventId = scopeId
    if (scopeType === "destination") scope.destinationId = scopeId

    const res = await createLink({ scope, expiresIn })
    if (res.error) {
      toast.error(res.error.message)
      return
    }

    const token = res.data?.createPortalLink.token
    if (token) {
      const url = `${window.location.origin}/portal/${token}`
      setShareUrl(url)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success("Link copied to clipboard")
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // silently ignore
    }
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setShareUrl("")
      setCopied(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share2 className="h-3.5 w-3.5 mr-1.5" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Share Link</DialogTitle>
        </DialogHeader>
        {!shareUrl ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Expires in</Label>
              <Select value={expiresIn} onValueChange={setExpiresIn}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">1 hour</SelectItem>
                  <SelectItem value="6h">6 hours</SelectItem>
                  <SelectItem value="24h">24 hours</SelectItem>
                  <SelectItem value="72h">3 days</SelectItem>
                  <SelectItem value="168h">7 days</SelectItem>
                  <SelectItem value="720h">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreate} className="w-full">
              Generate Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Anyone with this link can view this {scopeType} (read-only). The link expires in {expiresIn}.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
